export const LOADING_TABLEAU = './media/v12/shardmeadow-loading-tableau-v12.webp';

export const TITLE_BOOT_ASSETS = Object.freeze([
  LOADING_TABLEAU,
  './media/v4/shardmeadow-title-panorama-v4.webp',
  './media/v14/shardmeadow-hearth-union-card-v14.webp',
  './media/v14/shardmeadow-grove-pact-card-v14.webp',
  './media/v14/shardmeadow-cairn-assembly-card-v14.webp',
  './media/v14/shardmeadow-tideglass-compact-card-v14.webp',
  './media/v14/shardmeadow-duskfen-covenant-card-v14.webp',
  './media/v14/shardmeadow-skyclave-confluence-card-v14.webp',
]);

export const GAME_UI_ASSETS = Object.freeze([
  './media/v6/shardmeadow-hud-frame-v6.webp',
  './media/v6/shardmeadow-faction-sigils-atlas-v6.webp',
  './media/v2/ui/icon-attack.webp',
  './media/v2/ui/icon-construct.webp',
  './media/v2/ui/icon-defend.webp',
  './media/v2/ui/icon-gather.webp',
  './media/v2/ui/icon-melee.webp',
  './media/v2/ui/icon-move.webp',
  './media/v2/ui/icon-production.webp',
  './media/v2/ui/icon-rally.webp',
  './media/v2/ui/icon-ranged.webp',
  './media/v2/ui/icon-repair.webp',
  './media/v2/ui/icon-worker.webp',
]);

const FONT_REQUESTS = Object.freeze([
  '700 20px Cinzel',
  '800 20px Cinzel',
  '400 16px "Barlow Condensed"',
  '700 16px "Barlow Condensed"',
  '800 16px "Barlow Condensed"',
]);

function escapeMarkup(value) {
  return String(value ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

export function loadingScreenMarkup({
  heading = 'Opening Shardmeadow',
  detail = 'Preparing the frontier',
  progress = 0,
} = {}) {
  const percent = Math.round(Math.max(0, Math.min(1, Number(progress) || 0)) * 100);
  return `<main class="loading-screen" id="main-content" aria-labelledby="loading-title" aria-busy="true">
    <img class="loading-art" src="${LOADING_TABLEAU}" alt="" decoding="async" fetchpriority="high" draggable="false">
    <div class="loading-atmosphere" aria-hidden="true"></div>
    <section class="loading-card">
      <div class="loading-emblem" aria-hidden="true"><i></i><span></span></div>
      <p class="loading-kicker">INCOPREA · SHARDMEADOW</p>
      <h1 id="loading-title">${escapeMarkup(heading)}</h1>
      <p class="loading-detail" id="loading-detail" aria-live="polite">${escapeMarkup(detail)}</p>
      <div class="loading-track" id="loading-progress" role="progressbar" aria-label="Game preparation" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${percent}" style="--load-progress:${percent}%"><span></span></div>
      <div class="loading-readout"><span id="loading-state">PREPARING</span><b id="loading-percent">${percent}%</b></div>
      <button class="quiet-action loading-retry" type="button" data-loading-action="retry" hidden>RETRY PREPARATION</button>
    </section>
  </main>`;
}

export function mountLoadingScreen(root, options = {}) {
  root.innerHTML = loadingScreenMarkup(options);
  const screen = root.querySelector('.loading-screen');
  const detail = root.querySelector('#loading-detail');
  const state = root.querySelector('#loading-state');
  const percent = root.querySelector('#loading-percent');
  const progress = root.querySelector('#loading-progress');
  const retry = root.querySelector('.loading-retry');

  return {
    update(value, nextDetail, nextState = 'PREPARING') {
      const amount = Math.round(Math.max(0, Math.min(1, Number(value) || 0)) * 100);
      screen?.setAttribute('aria-busy', amount < 100 ? 'true' : 'false');
      if (detail && nextDetail) detail.textContent = nextDetail;
      if (state) state.textContent = nextState;
      if (percent) percent.textContent = `${amount}%`;
      if (progress) {
        progress.style.setProperty('--load-progress', `${amount}%`);
        progress.setAttribute('aria-valuenow', String(amount));
      }
    },
    fail(message) {
      screen?.setAttribute('aria-busy', 'false');
      screen?.classList.add('loading-failed');
      if (detail) detail.textContent = message;
      if (state) state.textContent = 'PREPARATION INTERRUPTED';
      if (retry) retry.hidden = false;
    },
  };
}

export async function decodeImage(src, ImageCtor = globalThis.Image) {
  if (typeof ImageCtor !== 'function') throw new Error(`Image loading is unavailable for ${src}`);
  const image = new ImageCtor();
  image.decoding = 'async';
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = async () => {
      if (settled) return;
      settled = true;
      try {
        if (typeof image.decode === 'function') await image.decode();
        resolve(image);
      } catch (error) {
        reject(error instanceof Error ? error : new Error(`Could not decode ${src}`));
      }
    };
    image.onload = finish;
    image.onerror = () => {
      if (settled) return;
      settled = true;
      reject(new Error(`Could not load ${src}`));
    };
    image.src = src;
    if (image.complete && image.naturalWidth > 0) queueMicrotask(finish);
  });
}

export async function preloadImageAssets(sources, { ImageCtor = globalThis.Image, onProgress = () => {} } = {}) {
  const unique = [...new Set((sources || []).filter(Boolean))];
  let completed = 0;
  onProgress({ completed, total: unique.length, progress: unique.length ? 0 : 1, src: null });
  const results = await Promise.allSettled(unique.map(async src => {
    const image = await decodeImage(src, ImageCtor);
    completed += 1;
    onProgress({ completed, total: unique.length, progress: completed / Math.max(1, unique.length), src });
    return image;
  }));
  const failures = results.filter(result => result.status === 'rejected');
  if (failures.length) throw new AggregateError(failures.map(result => result.reason), `${failures.length} image asset${failures.length === 1 ? '' : 's'} could not be prepared`);
  return { loaded: results.length, total: unique.length };
}

export async function preloadFonts(fontSet = globalThis.document?.fonts) {
  if (!fontSet?.load) return { loaded: 0, total: 0 };
  await Promise.all(FONT_REQUESTS.map(request => fontSet.load(request)));
  if (fontSet.ready) await fontSet.ready;
  return { loaded: FONT_REQUESTS.length, total: FONT_REQUESTS.length };
}
